import { AllocationScenarioValuationModel } from 'api';
import { NewAllocationScenario } from 'common/types/allocation';
import { ValuationsApproach } from 'common/types/valuation';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { EquityAllocationCellCustomData } from 'pages/ValuationsAllocation/allocation/EquityAllocation/config';
import {
  ALLOCATION_SCENARIO_METHOD_CSE,
  ALLOCATION_SCENARIO_METHOD_OPM_KEY,
  ALLOCATION_SCENARIO_METHOD_SPECIFIED_SHARE_VALUES,
  ALLOCATION_SCENARIO_METHOD_WATERFALL,
  ALLOCATION_SCENARIO_TYPE_BACKSOLVE,
  ALLOCATION_SCENARIO_TYPE_BACKSOLVE_KEY,
  ALLOCATION_SCENARIO_TYPE_FUTURE_EXIT,
  ALLOCATION_SCENARIO_TYPE_FUTURE_EXIT_KEY,
  ALLOCATION_SCENARIO_TYPE_SPECIFIED_SHARE_VALUES,
  ALLOCATION_SCENARIO_TYPE_SPECIFIED_SHARE_VALUES_KEY,
} from 'pages/ValuationsAllocation/common/constants/allocation';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_FUTURE_EXIT_APPROACH,
  VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations/valuations';
import { ApproachTypes } from 'pages/ValuationsAllocation/components/';
import { UpdateMainSpreadsheetsParams } from 'pages/ValuationsAllocation/types';
import { isAllocationMethodCell } from 'pages/ValuationsAllocation/util/cellValidators';
import { findScenarioByReference } from 'pages/ValuationsAllocation/util/findScenarioByReference';
import { shouldCollapseOPMInputs } from 'pages/ValuationsAllocation/util/shouldCollapseOPMInputs';
import { getObjectValue } from 'utilities';
import { OnChangeType, UpdateAllocationScenariosWithRelated, UpdateOPMInputsParams } from '../types';

interface HandleAllocationMethodParams {
  cell: ICell<EquityAllocationCellCustomData>;
  allocationScenarios: AllocationScenarioValuationModel[];
  approaches: ValuationsApproach[];
  cells: any;
  onChange: OnChangeType;
  expandOPMInputs: () => void;
  collapseOPMInputs: () => void;
  handleAddApproach: (approachType: ApproachTypes) => string;
  updateAllocationScenariosWithRelated: UpdateAllocationScenariosWithRelated;
  updateMainSpreadsheets: (params?: UpdateMainSpreadsheetsParams) => void;
  updateOPMInputs: (params: UpdateOPMInputsParams) => Promise<void>;
  updateSecurities: (allocationScenarios: AllocationScenarioValuationModel[]) => any[];
}

/**
 * Handles changes to allocation method cells in the equity allocation sheet.
 * Manages OPM inputs, scenario creation for Backsolve/Future Exit/SSV, and securities updates.
 */
export const handleAllocationMethod = async (params: HandleAllocationMethodParams): Promise<void> => {
  const {
    cell,
    allocationScenarios,
    approaches,
    cells,
    onChange,
    expandOPMInputs,
    collapseOPMInputs,
    handleAddApproach,
    updateAllocationScenariosWithRelated,
    updateMainSpreadsheets,
    updateOPMInputs,
    updateSecurities,
  } = params;

  if (!isAllocationMethodCell(cell)) {
    return;
  }

  const { data: cellData, value } = getObjectValue(cell);
  const { isApproachWithOPM, scenarioId, scenarioRef } = getObjectValue(cellData as EquityAllocationCellCustomData);

  // Find the scenario
  const scenario = findScenarioByReference({
    allocationScenarios: allocationScenarios as NewAllocationScenario[],
    scenarioId,
    scenarioRef,
  });

  if (!scenario) {
    return;
  }

  const typedScenario = getObjectValue(scenario);

  // Handle when a scenario method is changed
  switch (value) {
    case ALLOCATION_SCENARIO_METHOD_OPM_KEY:
      expandOPMInputs();
      break;

    case ALLOCATION_SCENARIO_TYPE_BACKSOLVE_KEY:
      typedScenario.approach_uuid = handleAddApproach(VALUATIONS_BACKSOLVE_APPROACH);
      typedScenario.backsolve_valuation = null;
      typedScenario.scenario_method = ALLOCATION_SCENARIO_METHOD_WATERFALL;
      typedScenario.scenario_type = ALLOCATION_SCENARIO_TYPE_BACKSOLVE;
      return;

    case ALLOCATION_SCENARIO_TYPE_FUTURE_EXIT_KEY:
      typedScenario.approach_uuid = handleAddApproach(VALUATIONS_FUTURE_EXIT_APPROACH);
      typedScenario.future_exit = null;
      typedScenario.scenario_method = ALLOCATION_SCENARIO_METHOD_CSE;
      typedScenario.scenario_type = ALLOCATION_SCENARIO_TYPE_FUTURE_EXIT;
      return;

    case ALLOCATION_SCENARIO_TYPE_SPECIFIED_SHARE_VALUES_KEY:
      typedScenario.approach_uuid = handleAddApproach(VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH);
      typedScenario.scenario_method = ALLOCATION_SCENARIO_METHOD_SPECIFIED_SHARE_VALUES;
      typedScenario.scenario_type = ALLOCATION_SCENARIO_TYPE_SPECIFIED_SHARE_VALUES;
      typedScenario.specified_share_values = null;
      return;

    default:
      if (shouldCollapseOPMInputs(allocationScenarios)) {
        collapseOPMInputs();
      }
      break;
  }

  const approach = approaches.find(a => a.panelId === typedScenario.approach_uuid);
  if (
    approach?.approach_type === VALUATIONS_BACKSOLVE_APPROACH ||
    approach?.approach_type === VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH
  ) {
    return;
  }

  // Update Securities
  const updatedSecurities = updateSecurities(allocationScenarios);

  // Resets some properties in valuation summary and equity allocation spreadsheets, then calls resetConfigurations
  updateMainSpreadsheets({ updatedSecurities });

  // Update Risk Free Rates based on OPM Inputs
  await updateOPMInputs({
    allocationScenarios,
    approaches,
  });

  await updateAllocationScenariosWithRelated(
    {
      allocationScenarios,
      approaches,
    },
    cells,
    onChange
  );

  if (isApproachWithOPM) {
    expandOPMInputs();
  }
};
